import React, { useEffect, useRef } from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import grapesjsCkeditor from 'grapesjs-plugin-ckeditor';

// Clean pasted HTML from Word/external sources while preserving layout structure
const cleanPastedHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const removeSelectors = [
    'meta', 'link', 'style', 'xml', 'o\\:p', 'v\\:shapetype', 'v\\:shape',
    'v\\:imagedata', 'v\\:fill', 'v\\:stroke', 'v\\:path', 'v\\:textbox',
  ];
  removeSelectors.forEach(sel => {
    try { doc.querySelectorAll(sel).forEach(el => el.remove()); } catch(e) { /* skip */ }
  });

  const removeComments = (node) => {
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_COMMENT);
    const comments = [];
    while (walker.nextNode()) comments.push(walker.currentNode);
    comments.forEach(c => c.parentNode?.removeChild(c));
  };
  removeComments(doc.body);

  doc.querySelectorAll('[class]').forEach(el => {
    const classes = el.className.split?.(' ') || [];
    const cleaned = classes.filter(c => !c.startsWith('Mso') && !c.startsWith('mso'));
    if (cleaned.length === 0) el.removeAttribute('class');
    else el.className = cleaned.join(' ');
  });

  doc.querySelectorAll('[style]').forEach(el => {
    const style = el.getAttribute('style') || '';
    const cleaned = style.split(';').map(s => s.trim())
      .filter(s => s && !s.startsWith('mso-') && !s.startsWith('tab-stops')).join('; ');
    if (cleaned) el.setAttribute('style', cleaned);
    else el.removeAttribute('style');
  });

  doc.querySelectorAll('table').forEach(table => {
    table.setAttribute('style', (table.getAttribute('style') || '') + '; border-collapse: collapse; width: 100%;');
    table.querySelectorAll('td, th').forEach(cell => {
      const s = cell.getAttribute('style') || '';
      cell.setAttribute('style', s + '; padding: 8px; border: 1px solid #ddd;');
    });
  });

  // Explicitly mark leaf text elements for GrapeJS text component type
  ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'figcaption'].forEach(tag => {
    doc.querySelectorAll(tag).forEach(el => {
      el.setAttribute('data-gjs-type', `editable-${tag}`);
      el.setAttribute('data-gjs-editable', 'true');
    });
  });

  // Mark td/th/li as editable containers (not text type, so children are still parsed)
  ['td', 'th', 'li'].forEach(tag => {
    doc.querySelectorAll(tag).forEach(el => {
      el.setAttribute('data-gjs-type', `container-${tag}`);
      el.setAttribute('data-gjs-editable', 'true');
    });
  });

  const result = doc.body.innerHTML
    .replace(/<!--\[if[\s\S]*?endif\]-->/gi, '')
    .replace(/<!\[if[\s\S]*?<!\[endif\]>/gi, '')
    .replace(/\0/g, '').trim();

  return `<div style="padding: 20px; margin-bottom: 16px;">${result}</div>`;
};

// Inline SVG icon helper (Lucide-style, 20x20, stroke-based)
const icon = (paths, size = 20) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;

const ICONS = {
  undo:       icon('<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>'),
  redo:       icon('<polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/>'),
  eye:        icon('<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>'),
  maximize:   icon('<polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>'),
  clipboard:  icon('<rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>'),
  clearAll:   icon('<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>'),
  blocks:     icon('<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>'),
  pencil:     icon('<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>'),
  code:       icon('<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>'),
  download:   icon('<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>'),
};

// Block icons (bigger, 32x32 viewBox for more detail)
const blockIcon = (paths) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;

const BLOCK_ICONS = {
  hero:       blockIcon('<rect x="3" y="5" width="26" height="22" rx="3"/><line x1="9" y1="12" x2="23" y2="12"/><line x1="11" y1="16" x2="21" y2="16"/><rect x="12" y="20" width="8" height="3" rx="1.5"/>'),
  text:       blockIcon('<line x1="5" y1="8" x2="27" y2="8"/><line x1="5" y1="13" x2="24" y2="13"/><line x1="5" y1="18" x2="20" y2="18"/><line x1="5" y1="23" x2="15" y2="23"/>'),
  image:      blockIcon('<rect x="4" y="6" width="24" height="20" rx="3"/><circle cx="10" cy="13" r="2.5" fill="currentColor" stroke="none"/><path d="M4 22l7-7 5 5 3-3 9 9" stroke-width="1.5"/>'),
  imageText:  blockIcon('<rect x="3" y="7" width="12" height="18" rx="2"/><line x1="19" y1="10" x2="29" y2="10"/><line x1="19" y1="14" x2="27" y2="14"/><line x1="19" y1="18" x2="25" y2="18"/><rect x="19" y="21" width="7" height="2.5" rx="1.2"/>'),
  col2:       blockIcon('<rect x="3" y="5" width="12" height="22" rx="2.5"/><rect x="17" y="5" width="12" height="22" rx="2.5"/>'),
  col3:       blockIcon('<rect x="2" y="5" width="8" height="22" rx="2"/><rect x="12" y="5" width="8" height="22" rx="2"/><rect x="22" y="5" width="8" height="22" rx="2"/>'),
  divider:    blockIcon('<line x1="4" y1="16" x2="28" y2="16" stroke-width="2"/>'),
  spacer:     blockIcon('<line x1="6" y1="11" x2="26" y2="11" stroke-dasharray="2 2"/><line x1="6" y1="21" x2="26" y2="21" stroke-dasharray="2 2"/><line x1="16" y1="13" x2="16" y2="19" stroke-dasharray="2 2"/>'),
  button:     blockIcon('<rect x="6" y="11" width="20" height="10" rx="5"/><line x1="12" y1="16" x2="20" y2="16"/>'),
  video:      blockIcon('<rect x="4" y="6" width="24" height="20" rx="3"/><polygon points="13,11 22,16 13,21" fill="currentColor" stroke="none"/>'),
};

const DevGrapesScreen = (props) => {
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);

  useEffect(() => {
    if (editorInstanceRef.current) return;

    editorInstanceRef.current = grapesjs.init({
      container: editorRef.current,
      height: 'calc(100vh - 64px)',
      width: '100%',
      plugins: [grapesjsCkeditor],
      pluginsOpts: {
        [grapesjsCkeditor]: {
          ckeditor: 'https://cdn.ckeditor.com/4.25.1-lts/standard-all/ckeditor.js',
          position: 'center',
          options: {
            startupFocus: true,
            extraAllowedContent: '*(*);*{*}',
            allowedContent: true,
            enterMode: 2,
            extraPlugins: 'sharedspace,justify,colorbutton,panelbutton,font,pastefromword,pastetext,clipboard,pastetools,indentblock',
            forcePasteAsPlainText: false,
            pasteFromWordRemoveFontStyles: false,
            pasteFromWordRemoveStyles: false,
            pasteFromWord_keepStructure: true,
            pasteFilter: null,
            toolbar: [
              { name: 'styles', items: ['Font', 'FontSize', 'Format'] },
              { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', 'RemoveFormat'] },
              { name: 'align', items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'] },
              { name: 'paragraph', items: ['NumberedList', 'BulletedList', 'Indent', 'Outdent'] },
              { name: 'colors', items: ['TextColor', 'BGColor'] },
              { name: 'links', items: ['Link', 'Unlink'] },
              { name: 'insert', items: ['Table', 'HorizontalRule', 'SpecialChar'] },
              { name: 'paste', items: ['PasteFromWord'] },
            ],
          },
        },
      },
      storageManager: false,
      panels: { defaults: [] },
      deviceManager: { devices: [] },
      styleManager: {
        appendTo: '#grapes-styles-inner',
        sectors: [
          {
            name: 'Typography',
            open: true,
            properties: [
              { property: 'font-family', type: 'select',
                options: [
                  { id: 'Arial, Helvetica, sans-serif', label: 'Arial' },
                  { id: "'Courier New', Courier, monospace", label: 'Courier New' },
                  { id: 'Georgia, serif', label: 'Georgia' },
                  { id: "'Times New Roman', Times, serif", label: 'Times New Roman' },
                  { id: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
                  { id: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
                  { id: "'Segoe UI', sans-serif", label: 'Segoe UI' },
                ],
              },
              { property: 'font-size', type: 'number', units: ['px', 'em', 'rem', '%'], min: 0 },
              { property: 'font-weight', type: 'select',
                options: [
                  { id: '300', label: 'Light' },
                  { id: '400', label: 'Normal' },
                  { id: '500', label: 'Medium' },
                  { id: '600', label: 'Semi Bold' },
                  { id: '700', label: 'Bold' },
                  { id: '800', label: 'Extra Bold' },
                ],
              },
              { property: 'line-height', type: 'number', units: ['px', 'em', ''], min: 0 },
              { property: 'letter-spacing', type: 'number', units: ['px', 'em'], min: -10 },
              { property: 'color', type: 'color' },
              { property: 'text-align', type: 'radio',
                options: [
                  { id: 'left', label: 'Left', className: 'fa fa-align-left' },
                  { id: 'center', label: 'Center', className: 'fa fa-align-center' },
                  { id: 'right', label: 'Right', className: 'fa fa-align-right' },
                  { id: 'justify', label: 'Justify', className: 'fa fa-align-justify' },
                ],
              },
              { property: 'text-decoration', type: 'select',
                options: [
                  { id: 'none', label: 'None' },
                  { id: 'underline', label: 'Underline' },
                  { id: 'line-through', label: 'Line Through' },
                  { id: 'overline', label: 'Overline' },
                ],
              },
              { property: 'text-transform', type: 'select',
                options: [
                  { id: 'none', label: 'None' },
                  { id: 'uppercase', label: 'Uppercase' },
                  { id: 'lowercase', label: 'Lowercase' },
                  { id: 'capitalize', label: 'Capitalize' },
                ],
              },
            ],
          },
          {
            name: 'Layout',
            open: false,
            properties: [
              { property: 'display', type: 'select',
                options: [
                  { id: 'block', label: 'Block' },
                  { id: 'inline-block', label: 'Inline Block' },
                  { id: 'flex', label: 'Flex' },
                  { id: 'none', label: 'Hidden' },
                ],
              },
              { property: 'flex-direction', type: 'select',
                options: [
                  { id: 'row', label: 'Row' },
                  { id: 'column', label: 'Column' },
                ],
              },
              { property: 'justify-content', type: 'select',
                options: [
                  { id: 'flex-start', label: 'Start' },
                  { id: 'center', label: 'Center' },
                  { id: 'flex-end', label: 'End' },
                  { id: 'space-between', label: 'Space Between' },
                  { id: 'space-around', label: 'Space Around' },
                ],
              },
              { property: 'align-items', type: 'select',
                options: [
                  { id: 'flex-start', label: 'Start' },
                  { id: 'center', label: 'Center' },
                  { id: 'flex-end', label: 'End' },
                  { id: 'stretch', label: 'Stretch' },
                ],
              },
              { property: 'gap', type: 'number', units: ['px', 'em', 'rem'], min: 0 },
            ],
          },
          {
            name: 'Spacing',
            open: false,
            properties: [
              { property: 'padding', type: 'composite',
                properties: [
                  { property: 'padding-top', type: 'number', units: ['px', 'em', '%'], min: 0 },
                  { property: 'padding-right', type: 'number', units: ['px', 'em', '%'], min: 0 },
                  { property: 'padding-bottom', type: 'number', units: ['px', 'em', '%'], min: 0 },
                  { property: 'padding-left', type: 'number', units: ['px', 'em', '%'], min: 0 },
                ],
              },
              { property: 'margin', type: 'composite',
                properties: [
                  { property: 'margin-top', type: 'number', units: ['px', 'em', '%', 'auto'] },
                  { property: 'margin-right', type: 'number', units: ['px', 'em', '%', 'auto'] },
                  { property: 'margin-bottom', type: 'number', units: ['px', 'em', '%', 'auto'] },
                  { property: 'margin-left', type: 'number', units: ['px', 'em', '%', 'auto'] },
                ],
              },
            ],
          },
          {
            name: 'Size',
            open: false,
            properties: [
              { property: 'width', type: 'number', units: ['px', '%', 'em', 'auto'] },
              { property: 'max-width', type: 'number', units: ['px', '%', 'em'], min: 0 },
              { property: 'min-height', type: 'number', units: ['px', '%', 'em'], min: 0 },
              { property: 'height', type: 'number', units: ['px', '%', 'em', 'auto'] },
            ],
          },
          {
            name: 'Background',
            open: false,
            properties: [
              { property: 'background-color', type: 'color' },
              { property: 'border-radius', type: 'number', units: ['px', '%', 'em'], min: 0 },
            ],
          },
          {
            name: 'Border',
            open: false,
            properties: [
              { property: 'border', type: 'composite',
                properties: [
                  { property: 'border-width', type: 'number', units: ['px'], min: 0 },
                  { property: 'border-style', type: 'select',
                    options: [
                      { id: 'none', label: 'None' },
                      { id: 'solid', label: 'Solid' },
                      { id: 'dashed', label: 'Dashed' },
                      { id: 'dotted', label: 'Dotted' },
                    ],
                  },
                  { property: 'border-color', type: 'color' },
                ],
              },
            ],
          },
        ],
      },
      layerManager: { appendTo: null },
      traitManager: { appendTo: '#grapes-traits-inner' },
      blockManager: {
        appendTo: '#grapes-blocks-inner',
        blocks: [
          {
            id: 'hero-banner',
            label: 'Hero Banner',
            category: 'Sections',
            content: `<section style="padding:40px 20px;text-align:center;background:#f5f5f5;border-radius:8px;margin-bottom:16px;">
              <h1 style="margin:0 0 12px 0;font-size:28px;">Your Shop Title</h1>
              <p style="margin:0 0 16px 0;font-size:16px;color:#666;">Welcome to our shop. Edit this text to introduce your brand.</p>
              <a href="#" style="display:inline-block;padding:10px 24px;background:#0071BB;color:#fff;text-decoration:none;border-radius:4px;font-size:14px;">Shop Now</a>
            </section>`,
            media: BLOCK_ICONS.hero,
          },
          {
            id: 'image-text',
            label: 'Image + Text',
            category: 'Sections',
            content: `<div style="display:flex;gap:20px;padding:20px;margin-bottom:16px;align-items:center;flex-wrap:wrap;">
              <div style="flex:1;min-width:200px;">
                <img src="https://via.placeholder.com/400x300?text=Image" alt="Image" style="max-width:100%;height:auto;border-radius:8px;"/>
              </div>
              <div style="flex:1;min-width:200px;">
                <h2 style="margin:0 0 8px 0;font-size:20px;">Product or Feature</h2>
                <p style="margin:0 0 12px 0;font-size:14px;line-height:1.6;color:#555;">Describe your product or feature here.</p>
                <a href="#" style="display:inline-block;padding:8px 20px;background:#0071BB;color:#fff;text-decoration:none;border-radius:4px;font-size:13px;">Learn More</a>
              </div>
            </div>`,
            media: BLOCK_ICONS.imageText,
          },
          {
            id: 'two-columns',
            label: '2 Columns',
            category: 'Layout',
            content: `<div style="display:flex;gap:20px;padding:20px;margin-bottom:16px;flex-wrap:wrap;">
              <div style="flex:1;min-width:200px;padding:16px;background:#fafafa;border-radius:8px;">
                <h3 style="margin:0 0 8px 0;">Column 1</h3>
                <p style="margin:0;font-size:14px;color:#555;">Edit this column content.</p>
              </div>
              <div style="flex:1;min-width:200px;padding:16px;background:#fafafa;border-radius:8px;">
                <h3 style="margin:0 0 8px 0;">Column 2</h3>
                <p style="margin:0;font-size:14px;color:#555;">Edit this column content.</p>
              </div>
            </div>`,
            media: BLOCK_ICONS.col2,
          },
          {
            id: 'three-columns',
            label: '3 Columns',
            category: 'Layout',
            content: `<div style="display:flex;gap:16px;padding:20px;margin-bottom:16px;flex-wrap:wrap;">
              <div style="flex:1;min-width:150px;padding:16px;background:#fafafa;border-radius:8px;text-align:center;">
                <h3 style="margin:0 0 8px 0;">Column 1</h3>
                <p style="margin:0;font-size:14px;color:#555;">Content here.</p>
              </div>
              <div style="flex:1;min-width:150px;padding:16px;background:#fafafa;border-radius:8px;text-align:center;">
                <h3 style="margin:0 0 8px 0;">Column 2</h3>
                <p style="margin:0;font-size:14px;color:#555;">Content here.</p>
              </div>
              <div style="flex:1;min-width:150px;padding:16px;background:#fafafa;border-radius:8px;text-align:center;">
                <h3 style="margin:0 0 8px 0;">Column 3</h3>
                <p style="margin:0;font-size:14px;color:#555;">Content here.</p>
              </div>
            </div>`,
            media: BLOCK_ICONS.col3,
          },
          {
            id: 'text-block',
            label: 'Text',
            category: 'Elements',
            content: `<div style="padding:20px;margin-bottom:16px;">
              <h2 style="margin:0 0 8px 0;font-size:22px;">Section Title</h2>
              <p style="margin:0;font-size:14px;line-height:1.6;color:#333;">Add your content here.</p>
            </div>`,
            media: BLOCK_ICONS.text,
          },
          {
            id: 'image-block',
            label: 'Image',
            category: 'Elements',
            content: `<div style="padding:10px;text-align:center;margin-bottom:16px;">
              <img src="https://via.placeholder.com/600x300?text=Your+Image" alt="Image" style="max-width:100%;height:auto;border-radius:8px;"/>
            </div>`,
            media: BLOCK_ICONS.image,
          },
          {
            id: 'button-block',
            label: 'Button',
            category: 'Elements',
            content: `<div style="text-align:center;padding:10px;margin-bottom:16px;">
              <a href="#" style="display:inline-block;padding:12px 32px;background:#0071BB;color:#fff;text-decoration:none;border-radius:4px;font-size:15px;font-weight:bold;">Click Here</a>
            </div>`,
            media: BLOCK_ICONS.button,
          },
          {
            id: 'video-block',
            label: 'Video',
            category: 'Elements',
            content: `<div style="padding:10px;text-align:center;margin-bottom:16px;">
              <video controls style="max-width:100%;border-radius:8px;">
                <source src="" type="video/mp4"/>
                Your browser does not support the video tag.
              </video>
            </div>`,
            media: BLOCK_ICONS.video,
          },
          {
            id: 'divider',
            label: 'Divider',
            category: 'Elements',
            content: '<hr style="border:none;border-top:1px solid #ddd;margin:20px 0;"/>',
            media: BLOCK_ICONS.divider,
          },
          {
            id: 'spacer',
            label: 'Spacer',
            category: 'Elements',
            content: '<div style="height:40px;"></div>',
            media: BLOCK_ICONS.spacer,
          },
        ],
      },
    });

    const editor = editorInstanceRef.current;

    // Register LEAF text tags as "text" component type so double-click activates the RTE.
    // Important: only leaf text elements (p, h1-h6, etc.), NOT containers like td/th/li
    // because "text" type treats inner HTML as raw text and won't parse child components.
    const leafTextTags = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'FIGCAPTION'];
    leafTextTags.forEach(tag => {
      const typeName = `editable-${tag.toLowerCase()}`;
      editor.DomComponents.addType(typeName, {
        isComponent: (el) => el.tagName === tag ? { type: typeName } : false,
        extend: 'text',
        model: {
          defaults: {
            tagName: tag.toLowerCase(),
            editable: true,
          },
        },
      });
    });

    // For table cells and list items: keep them as containers but make their
    // direct text content editable via double-click event handler below
    ['TD', 'TH', 'LI'].forEach(tag => {
      const typeName = `container-${tag.toLowerCase()}`;
      editor.DomComponents.addType(typeName, {
        isComponent: (el) => el.tagName === tag ? { type: typeName } : false,
        model: {
          defaults: {
            tagName: tag.toLowerCase(),
            droppable: true,
            selectable: true,
            hoverable: true,
            editable: true,
          },
        },
      });
    });

    // Fallback: if double-click on a component that has no children components
    // but contains text, enable RTE editing on it
    editor.on('component:dblclick', (component) => {
      const children = component.components();
      const el = component.getEl();
      if (!el) return;
      // If the component has no GrapeJS child components but has text content, make it editable
      if ((!children || children.length === 0) && el.textContent && el.textContent.trim().length > 0) {
        if (!component.get('editable')) {
          component.set('editable', true);
        }
      }
    });

    // Sidebar panel switching helper
    const switchPanel = (panelId) => {
      document.querySelectorAll('.grapes-panel').forEach(p => p.classList.remove('active'));
      // Only toggle active class on panel-switch buttons, not all toolbar buttons
      document.querySelectorAll('.sft-toolbar-btn[data-panel]').forEach(b => b.classList.remove('active'));
      const panel = document.getElementById(`panel-${panelId}`);
      const btn = document.getElementById(`btn-${panelId}`);
      if (panel) panel.classList.add('active');
      if (btn) btn.classList.add('active');
    };

    // Show/hide the empty message and auto-switch to style panel on selection
    editor.on('component:selected', () => {
      const emptyMsg = document.getElementById('style-empty-msg');
      if (emptyMsg) emptyMsg.style.display = 'none';
      switchPanel('style');
    });

    editor.on('component:deselected', () => {
      setTimeout(() => {
        if (!editor.getSelected()) {
          const emptyMsg = document.getElementById('style-empty-msg');
          if (emptyMsg) emptyMsg.style.display = 'block';
        }
      }, 50);
    });

    // Wire up toolbar buttons (our own #sft-toolbar div, outside of GrapeJS)
    const wireToolbar = () => {
      const toolbar = document.getElementById('sft-toolbar');
      if (!toolbar) return;
      toolbar.querySelectorAll('[data-action]').forEach(btnEl => {
        btnEl.onclick = async () => {
          const action = btnEl.getAttribute('data-action');
          const cmd = btnEl.getAttribute('data-cmd');
          const panel = btnEl.getAttribute('data-panel');
          if (action === 'panel') {
            switchPanel(panel);
            return;
          }
          if (action === 'cmd' && cmd) {
            editor.runCommand(cmd);
            return;
          }
          if (action === 'paste') {
            try {
              const clipboardItems = await navigator.clipboard.read();
              let html = '';
              for (const item of clipboardItems) {
                if (item.types.includes('text/html')) {
                  const blob = await item.getType('text/html');
                  html = await blob.text();
                  break;
                }
              }
              if (html) {
                editor.getWrapper().components().add(cleanPastedHtml(html));
              } else {
                const text = await navigator.clipboard.readText();
                if (text) editor.getWrapper().components().add(`<div style="padding:20px;margin-bottom:16px;"><p>${text}</p></div>`);
              }
            } catch (err) {
              alert('Please use Ctrl+V to paste, or allow clipboard access in your browser settings.');
            }
          }
          if (action === 'code') {
            const h = editor.getHtml();
            const c = editor.getCss();
            editor.Modal.setTitle('Source Code');
            editor.Modal.setContent(`<div style="display:flex;flex-direction:column;gap:12px;min-width:500px;">
              <div><label style="font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">HTML</label>
              <textarea style="width:100%;height:200px;font-family:monospace;font-size:12px;padding:12px;border:1px solid #e2e8f0;border-radius:8px;resize:vertical;margin-top:4px;" readonly>${h.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea></div>
              <div><label style="font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">CSS</label>
              <textarea style="width:100%;height:150px;font-family:monospace;font-size:12px;padding:12px;border:1px solid #e2e8f0;border-radius:8px;resize:vertical;margin-top:4px;" readonly>${c.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea></div>
            </div>`);
            editor.Modal.open();
          }
          if (action === 'export') {
            const h = editor.getHtml();
            const c = editor.getCss();
            const blob = new Blob([`<!DOCTYPE html>\n<html>\n<head>\n<style>\n${c}\n</style>\n</head>\n<body>\n${h}\n</body>\n</html>`], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = 'page-export.html'; a.click();
            URL.revokeObjectURL(url);
          }
          if (action === 'clear') {
            if (window.confirm('Clear all content from the canvas?')) editor.DomComponents.clear();
          }
        };
      });
    };
    wireToolbar();

    // Escape key exits preview mode
    const exitPreview = (e) => {
      if (e.key === 'Escape') {
        editor.stopCommand('preview');
        editor.stopCommand('core:preview');
      }
    };
    document.addEventListener('keydown', exitPreview);

    editor.on('load', () => {
      const iframeDoc = editor.Canvas.getDocument();
      if (iframeDoc) {
        iframeDoc.addEventListener('keydown', exitPreview);

        // PowerPaste: intercept paste on canvas and insert as layout blocks
        iframeDoc.addEventListener('paste', (e) => {
          const activeEl = iframeDoc.activeElement;
          if (activeEl && (activeEl.contentEditable === 'true' || activeEl.closest?.('[contenteditable="true"]'))) {
            return;
          }
          const html = e.clipboardData?.getData('text/html');
          if (!html) return;
          e.preventDefault();
          e.stopPropagation();
          const cleanHtml = cleanPastedHtml(html);
          const wrapper = editor.getWrapper();
          const selected = editor.getSelected();
          if (selected) {
            const parent = selected.parent();
            const index = parent ? parent.components().indexOf(selected) + 1 : undefined;
            if (parent) parent.components().add(cleanHtml, { at: index });
            else wrapper.components().add(cleanHtml);
          } else {
            wrapper.components().add(cleanHtml);
          }
        });
      }
    });

    return () => {
      document.removeEventListener('keydown', exitPreview);
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className={props.screenWidth < 600 ? 'not_expanded' : 'expanded'} style={{ height: 'calc(100vh - 84px)', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        /* ===== RESET ALL GRAPES DEFAULTS ===== */
        .gjs-one-bg { background-color: #fff !important; }
        .gjs-two-color { color: #1e293b !important; }
        .gjs-three-bg { background-color: #fff !important; }
        .gjs-four-color, .gjs-four-color-h:hover { color: #0071BB !important; }

        /* ===== GLOBAL FONT ===== */
        .gjs-editor-cont, .gjs-editor-cont * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important;
        }

        /* ===== EDITOR CONTAINER ===== */
        .gjs-editor-cont {
          position: relative !important;
          width: 100% !important; height: 100% !important;
          background: #fff !important;
        }
        .gjs-editor {
          position: absolute !important;
          top: 52px; left: 0; right: 0; bottom: 0;
          background: #fff !important;
        }

        /* ===== HIDE ALL GRAPES PANELS (we use our own toolbar) ===== */
        .gjs-pn-panels {
          display: none !important;
          height: 0 !important;
          min-height: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
          overflow: hidden !important;
        }

        /* ===== OUR CUSTOM TOOLBAR ===== */
        #sft-toolbar {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 52px;
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 3px;
          padding: 0 12px;
          z-index: 20;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        }

        /* Custom toolbar buttons */
        .sft-toolbar-btn {
          width: 34px;
          height: 34px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition: all 0.15s ease;
          padding: 0;
          flex-shrink: 0;
        }
        .sft-toolbar-btn:hover {
          background: #f1f5f9;
          color: #334155;
        }
        .sft-toolbar-btn:active {
          background: #e2e8f0;
          transform: scale(0.95);
        }
        .sft-toolbar-btn svg {
          width: 18px;
          height: 18px;
        }
        .toolbar-sep {
          width: 1px;
          height: 20px;
          background: #e5e7eb;
          margin: 0 6px;
          flex-shrink: 0;
        }

        /* ===== CANVAS ===== */
        .gjs-cv-canvas {
          right: 260px !important;
          left: 0 !important;
          width: auto !important;
          background: #f8f9fb !important;
          top: 52px !important;
          bottom: 0 !important;
          height: auto !important;
        }
        .gjs-frame-wrapper {
          background: #f8f9fb !important;
          padding: 28px !important;
        }
        .gjs-frame {
          border-radius: 6px !important;
          box-shadow: 0 1px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04) !important;
          background: #fff !important;
        }

        /* Selection & hover */
        .gjs-selected { outline: 2px solid #0071BB !important; outline-offset: -1px; }
        .gjs-comp-selected { outline: 2px solid #0071BB !important; }
        .gjs-highlighter-sel { outline: 2px dashed rgba(0,113,187,0.4) !important; }

        /* Component toolbar (move, clone, delete) */
        .gjs-toolbar {
          background: #1e293b !important;
          border-radius: 8px !important;
          padding: 3px 4px !important;
          box-shadow: 0 4px 16px rgba(0,0,0,0.2) !important;
          border: none !important;
          gap: 1px !important;
        }
        .gjs-toolbar-item {
          width: 28px !important;
          height: 28px !important;
          font-size: 12px !important;
          color: #cbd5e1 !important;
          border-radius: 6px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.15s !important;
          border: none !important;
        }
        .gjs-toolbar-item:hover {
          background: rgba(255,255,255,0.12) !important;
          color: #fff !important;
        }

        /* Resize handles */
        .gjs-resizer-h { border: 2px solid #0071BB !important; }
        .gjs-resizer-h-tl, .gjs-resizer-h-tr, .gjs-resizer-h-bl, .gjs-resizer-h-br,
        .gjs-resizer-h-tc, .gjs-resizer-h-bc, .gjs-resizer-h-cl, .gjs-resizer-h-cr {
          width: 8px !important; height: 8px !important;
          border-radius: 50% !important;
          background: #0071BB !important;
          border: 2px solid #fff !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2) !important;
          margin: -4px !important;
        }

        /* Component badge */
        .gjs-badge {
          background: #0071BB !important;
          color: #fff !important;
          font-size: 10px !important;
          font-weight: 600 !important;
          border-radius: 4px !important;
          padding: 2px 8px !important;
          letter-spacing: 0.3px !important;
        }

        /* Drag placeholder */
        .gjs-placeholder { border-color: #0071BB !important; }
        .gjs-placeholder-int {
          background: rgba(0,113,187,0.05) !important;
          border: 2px dashed #0071BB !important;
          border-radius: 6px !important;
        }

        /* Block cards */
        #grapes-blocks-inner .gjs-block {
          width: calc(50% - 6px) !important;
          min-height: 78px;
          margin: 3px !important;
          padding: 12px 4px 8px !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 10px !important;
          cursor: grab !important;
          display: inline-flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 11px !important;
          color: #64748b !important;
          font-weight: 500 !important;
          background: #fff !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          box-shadow: none !important;
          text-align: center !important;
          vertical-align: top !important;
        }
        #grapes-blocks-inner .gjs-block:hover {
          border-color: #93c5fd !important;
          background: #f0f7ff !important;
          box-shadow: 0 2px 8px rgba(0,113,187,0.08) !important;
          transform: translateY(-1px);
        }
        #grapes-blocks-inner .gjs-block:active {
          transform: translateY(0);
          border-color: #0071BB !important;
        }
        #grapes-blocks-inner .gjs-block svg {
          color: #94a3b8 !important;
          margin-bottom: 6px;
          transition: color 0.2s;
        }
        #grapes-blocks-inner .gjs-block:hover svg {
          color: #3b82f6 !important;
        }
        #grapes-blocks-inner .gjs-block-label {
          line-height: 1.2;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        /* Block category headers */
        #grapes-blocks-inner .gjs-block-category {
          margin-bottom: 0 !important;
          border: none !important;
          background: transparent !important;
        }
        #grapes-blocks-inner .gjs-block-category .gjs-title {
          font-size: 10px !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.8px !important;
          padding: 16px 6px 6px !important;
          color: #94a3b8 !important;
          border-bottom: none !important;
          background: transparent !important;
          cursor: default !important;
        }
        #grapes-blocks-inner .gjs-block-category .gjs-caret-icon { display: none !important; }
        #grapes-blocks-inner .gjs-blocks-c { display: flex !important; flex-wrap: wrap !important; }

        /* ===== HIDE UNUSED ===== */
        .gjs-pn-views-container, .gjs-pn-views, .gjs-pn-devices-c { display: none !important; }

        /* ===== MODALS ===== */
        .gjs-mdl-dialog {
          border-radius: 14px !important;
          box-shadow: 0 16px 48px rgba(0,0,0,0.12) !important;
          border: 1px solid #e5e7eb !important;
          overflow: hidden !important;
        }
        .gjs-mdl-header {
          background: #fff !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 16px 24px !important;
          font-weight: 600 !important;
          font-size: 15px !important;
          color: #1e293b !important;
        }
        .gjs-mdl-content { padding: 24px !important; background: #fff !important; }
        .gjs-mdl-btn-close { font-size: 18px !important; color: #94a3b8 !important; }
        .gjs-mdl-btn-close:hover { color: #1e293b !important; }

        /* ===== RTE TOOLBAR ===== */
        .gjs-rte-toolbar {
          background: #fff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 10px !important;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1) !important;
          padding: 4px 6px !important;
        }
        .gjs-rte-actionbar .gjs-rte-action {
          border: none !important;
          border-radius: 6px !important;
          margin: 1px !important;
          color: #64748b !important;
          transition: all 0.15s !important;
        }
        .gjs-rte-actionbar .gjs-rte-action:hover {
          background: #f1f5f9 !important;
          color: #334155 !important;
        }
        .gjs-rte-active { background: #eff6ff !important; color: #0071BB !important; }

        /* ===== TOOLBAR ACTIVE & DANGER STATES ===== */
        .sft-toolbar-btn.active {
          background: #eff6ff;
          color: #0071BB;
        }
        .sft-toolbar-btn.danger { color: #94a3b8; }
        .sft-toolbar-btn.danger:hover { background: #fef2f2; color: #dc2626; }

        /* ===== SIDEBAR ===== */
        #grapes-sidebar {
          position: absolute;
          right: 0; top: 52px; bottom: 0;
          width: 260px;
          background: #fff;
          border-left: 1px solid #e5e7eb;
          z-index: 5;
          display: flex;
          flex-direction: column;
        }

        /* Sidebar panels */
        .grapes-panel {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: thin;
          scrollbar-color: #d1d5db transparent;
          display: none;
        }
        .grapes-panel::-webkit-scrollbar { width: 4px; }
        .grapes-panel::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
        .grapes-panel::-webkit-scrollbar-track { background: transparent; }
        .grapes-panel.active { display: block; }

        .sidebar-panel-header {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: #94a3b8;
          padding: 16px 16px 8px;
        }

        /* Blocks panel */
        #grapes-blocks-inner { padding: 4px 12px 20px; }

        /* Style panel */
        #grapes-styles-inner { padding: 0; }
        #grapes-traits-inner { padding: 0 16px 16px; }
        .grapes-style-empty {
          padding: 40px 20px;
          text-align: center;
          color: #94a3b8;
          font-size: 12px;
        }
        .grapes-style-empty svg { margin-bottom: 12px; color: #d1d5db; }

        /* ===== STYLE MANAGER STYLING ===== */
        .gjs-sm-sector {
          border-bottom: 1px solid #f1f3f5 !important;
          background: transparent !important;
        }
        .gjs-sm-sector .gjs-sm-sector-title {
          font-size: 11px !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          color: #475569 !important;
          padding: 14px 16px 10px !important;
          background: transparent !important;
          border: none !important;
          cursor: pointer !important;
        }
        .gjs-sm-sector .gjs-sm-sector-caret {
          color: #94a3b8 !important;
          font-size: 10px !important;
        }
        .gjs-sm-properties {
          padding: 4px 16px 12px !important;
          background: transparent !important;
        }
        .gjs-sm-property {
          margin-bottom: 8px !important;
        }
        .gjs-sm-property .gjs-sm-label {
          font-size: 11px !important;
          color: #64748b !important;
          font-weight: 500 !important;
          margin-bottom: 4px !important;
        }
        /* Style manager inputs */
        .gjs-sm-property .gjs-field,
        .gjs-sm-property .gjs-sm-input-holder {
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 6px !important;
          color: #334155 !important;
          font-size: 12px !important;
          min-height: 32px !important;
        }
        .gjs-sm-property .gjs-field:focus-within {
          border-color: #0071BB !important;
          box-shadow: 0 0 0 2px rgba(0,113,187,0.1) !important;
        }
        .gjs-sm-property input,
        .gjs-sm-property select {
          background: transparent !important;
          border: none !important;
          color: #334155 !important;
          font-size: 12px !important;
          padding: 6px 8px !important;
        }
        .gjs-field-arrows { display: none !important; }
        .gjs-sm-property .gjs-field-units {
          background: transparent !important;
          border: none !important;
          border-left: 1px solid #e2e8f0 !important;
          color: #94a3b8 !important;
          font-size: 11px !important;
        }
        /* Radio buttons (text-align) */
        .gjs-sm-property .gjs-radio-items {
          display: flex !important;
          gap: 2px !important;
        }
        .gjs-sm-property .gjs-radio-item {
          flex: 1 !important;
        }
        .gjs-sm-property .gjs-radio-item label {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          height: 32px !important;
          border-radius: 6px !important;
          border: 1px solid #e2e8f0 !important;
          background: #f8fafc !important;
          color: #64748b !important;
          cursor: pointer !important;
          transition: all 0.15s !important;
          font-size: 13px !important;
        }
        .gjs-sm-property .gjs-radio-item label:hover {
          background: #f1f5f9 !important;
          border-color: #cbd5e1 !important;
        }
        .gjs-sm-property .gjs-radio-item input:checked + label {
          background: #eff6ff !important;
          border-color: #0071BB !important;
          color: #0071BB !important;
        }
        /* Color picker */
        .gjs-sm-property .gjs-field-color {
          border-radius: 6px !important;
        }
        .gjs-sm-property .gjs-field-color .gjs-field-colorp {
          border-radius: 4px !important;
          width: 20px !important;
          height: 20px !important;
          margin: 6px !important;
        }
        /* Composite properties (padding/margin) */
        .gjs-sm-composite {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
        }
        .gjs-sm-composite .gjs-sm-property {
          margin-bottom: 4px !important;
        }

        /* ===== TRAIT MANAGER STYLING ===== */
        .gjs-trt-traits {
          padding: 0 !important;
        }
        .gjs-trt-trait {
          margin-bottom: 8px !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
        }
        .gjs-trt-trait .gjs-label {
          font-size: 11px !important;
          color: #64748b !important;
          font-weight: 500 !important;
        }
        .gjs-trt-trait input,
        .gjs-trt-trait select {
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 6px !important;
          color: #334155 !important;
          font-size: 12px !important;
          padding: 6px 8px !important;
          min-height: 32px !important;
        }
        .gjs-trt-trait input:focus,
        .gjs-trt-trait select:focus {
          border-color: #0071BB !important;
          box-shadow: 0 0 0 2px rgba(0,113,187,0.1) !important;
          outline: none !important;
        }
      `}</style>
      {/* Custom toolbar - completely outside GrapeJS */}
      <div id="sft-toolbar">
        <button className="sft-toolbar-btn active" id="btn-blocks" data-action="panel" data-panel="blocks" title="Blocks" dangerouslySetInnerHTML={{__html: ICONS.blocks}} />
        <button className="sft-toolbar-btn" id="btn-style" data-action="panel" data-panel="style" title="Style" dangerouslySetInnerHTML={{__html: ICONS.pencil}} />
        <div className="toolbar-sep" />
        <button className="sft-toolbar-btn" data-action="cmd" data-cmd="core:undo" title="Undo" dangerouslySetInnerHTML={{__html: ICONS.undo}} />
        <button className="sft-toolbar-btn" data-action="cmd" data-cmd="core:redo" title="Redo" dangerouslySetInnerHTML={{__html: ICONS.redo}} />
        <div className="toolbar-sep" />
        <button className="sft-toolbar-btn" data-action="cmd" data-cmd="core:preview" title="Preview" dangerouslySetInnerHTML={{__html: ICONS.eye}} />
        <button className="sft-toolbar-btn" data-action="cmd" data-cmd="core:fullscreen" title="Fullscreen" dangerouslySetInnerHTML={{__html: ICONS.maximize}} />
        <button className="sft-toolbar-btn" data-action="code" title="View code" dangerouslySetInnerHTML={{__html: ICONS.code}} />
        <div className="toolbar-sep" />
        <button className="sft-toolbar-btn" data-action="paste" title="Paste layout" dangerouslySetInnerHTML={{__html: ICONS.clipboard}} />
        <button className="sft-toolbar-btn" data-action="export" title="Export HTML" dangerouslySetInnerHTML={{__html: ICONS.download}} />
        <div style={{flex: 1}} />
        <button className="sft-toolbar-btn danger" data-action="clear" title="Clear all" dangerouslySetInnerHTML={{__html: ICONS.clearAll}} />
      </div>
      {/* GrapeJS editor container */}
      <div ref={editorRef} style={{ height: '100%', width: '100%' }} />
      {/* Sidebar */}
      <div id="grapes-sidebar">
        <div className="grapes-panel active" id="panel-blocks">
          <div className="sidebar-panel-header">Blocks</div>
          <div id="grapes-blocks-inner"></div>
        </div>
        <div className="grapes-panel" id="panel-style">
          <div className="sidebar-panel-header">Style</div>
          <div id="grapes-traits-inner"></div>
          <div id="grapes-styles-inner"></div>
          <div className="grapes-style-empty" id="style-empty-msg">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <p>Select an element on the canvas to edit its style</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevGrapesScreen;
